import { useState, useEffect } from 'react';
import { APPOINTMENT_TYPES } from '../../utils/appointmentTypes';
import api from '../../services/api';

export default function CreateAppointmentModal({ defaultDoctorId, onSubmit, onClose }) {
  const [doctors, setDoctors]   = useState([]);
  const [patients, setPatients] = useState([]);
  const [form, setForm] = useState({
    doctorId:      defaultDoctorId || '',
    patientId:     '',
    scheduledDate: '',
    duration:      20,
    type:          'video',
    reason:        '',
    notes:         '',
    paymentAmount: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/admin/doctors?limit=100'),
      // FIX: usar /admin/patients que devuelve Patient docs con user poblado,
      // en vez de /admin/users?role=patient que devuelve User docs sin perfil de paciente.
      api.get('/admin/patients?limit=200'),
    ]).then(([dr, pt]) => {
      setDoctors(dr.data.data);
      setPatients(pt.data.data);
    }).catch(() => setError('No se pudo cargar médicos o pacientes'));
  }, []);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.doctorId || !form.patientId || !form.scheduledDate || !form.reason.trim()) {
      setError('Completa todos los campos requeridos');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSubmit({
        ...form,
        duration:      Number(form.duration),
        paymentAmount: form.paymentAmount !== '' ? Number(form.paymentAmount) : undefined,
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Error al crear cita');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-header">
          <h2>Nueva cita manual</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit} className="create-appt-form">
          <div className="form-row">
            <label>Médico *
              <select value={form.doctorId} onChange={e => set('doctorId', e.target.value)} required>
                <option value="">-- Selecciona --</option>
                {doctors.map(d => (
                  <option key={d._id} value={d._id}>
                    {d.user?.profile?.firstName} {d.user?.profile?.lastName} — {d.specialization}
                  </option>
                ))}
              </select>
            </label>

            <label>Paciente *
              <select value={form.patientId} onChange={e => set('patientId', e.target.value)} required>
                <option value="">-- Selecciona --</option>
                {patients.map(p => (
                  <option key={p._id} value={p._id}>
                    {p.user?.profile?.firstName} {p.user?.profile?.lastName}
                    {p.user?.email ? ` (${p.user.email})` : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="form-row">
            <label>Fecha y hora *
              <input type="datetime-local" value={form.scheduledDate}
                onChange={e => set('scheduledDate', e.target.value)} required />
            </label>

            <label>Duración (min)
              <input type="number" value={form.duration} min={5} max={120} step={5}
                onChange={e => set('duration', e.target.value)} />
            </label>
          </div>

          <div className="form-row">
            <label>Tipo
              <select value={form.type} onChange={e => set('type', e.target.value)}>
                {APPOINTMENT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>

            <label>Tarifa (COP)
              <input type="number" value={form.paymentAmount} min={0}
                onChange={e => set('paymentAmount', e.target.value)}
                placeholder="Por defecto: tarifa del médico" />
            </label>
          </div>

          <label>Motivo *
            <input type="text" value={form.reason}
              onChange={e => set('reason', e.target.value)}
              placeholder="Ej: Consulta general, seguimiento..." required />
          </label>

          <label>Notas adicionales
            <textarea value={form.notes} rows={3}
              onChange={e => set('notes', e.target.value)}
              placeholder="Información adicional para el médico..." />
          </label>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Creando...' : 'Crear cita'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
